import { Select, Spin } from "antd"
import React, { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useDebouncedCallback } from "../../../../hooks/use-debounced-callback.ts"
import { getLeads } from "../../../Leads/api/services.ts"

const LEAD_SEARCH_DEBOUNCE_MS = 300
const LEAD_SEARCH_PAGE_SIZE = 20
const LEAD_PICKER_ID = "automation-preview-lead"

type LeadOption = { value: string; label: string }

// Which lead the preview renders for; empty means the server's sample values.
export function LeadPicker({
  value,
  disabled,
  onChange
}: {
  value: string | null
  disabled: boolean
  onChange: (leadId: string | null) => void
}) {
  const { t } = useTranslation()
  const [leadOptions, setLeadOptions] = useState<LeadOption[]>([])
  const [picked, setPicked] = useState<LeadOption | null>(null)
  const [searching, setSearching] = useState(false)
  const latestSearch = useRef(0)

  const onSearch = useDebouncedCallback(async (term: string) => {
    const searchId = ++latestSearch.current
    setSearching(true)
    try {
      const response = await getLeads({
        page: 1,
        page_size: LEAD_SEARCH_PAGE_SIZE,
        q: term || null
      })
      if (searchId !== latestSearch.current) return
      setLeadOptions(
        response.ok
          ? (response.data?.results ?? []).map((lead) => ({
              value: lead.id,
              label: lead.lead_name || lead.customer_name || lead.id
            }))
          : []
      )
    } catch {
      if (searchId === latestSearch.current) setLeadOptions([])
    } finally {
      if (searchId === latestSearch.current) setSearching(false)
    }
  }, LEAD_SEARCH_DEBOUNCE_MS)

  // A later search's results need not contain the picked lead, and a Select
  // with no option for its value shows the raw id.
  const offered = useMemo(
    () =>
      picked &&
      value === picked.value &&
      !leadOptions.some((option) => option.value === picked.value)
        ? [picked, ...leadOptions]
        : leadOptions,
    [leadOptions, picked, value]
  )

  return (
    <div className={"flex flex-col gap-1"}>
      <label htmlFor={LEAD_PICKER_ID} className={"text-sm text-gray-600"}>
        {t("settingsAutomation.review.leadPicker.label")}
      </label>
      <Select
        id={LEAD_PICKER_ID}
        disabled={disabled}
        value={value ?? undefined}
        allowClear
        showSearch
        filterOption={false}
        placeholder={t("settingsAutomation.review.leadPicker.placeholder")}
        notFoundContent={searching ? <Spin size={"small"} /> : null}
        options={offered}
        onSearch={onSearch}
        onFocus={() => {
          if (!leadOptions.length) onSearch("")
        }}
        onChange={(next?: string) => {
          setPicked(offered.find((option) => option.value === next) ?? null)
          onChange(next ?? null)
        }}
      />
    </div>
  )
}
